<template>
  <div
    v-if="appEditable"
    class="interaction-mode-cont d-flex align-center ga-md-4 px-4 mb-3"
    :class="{ 'bg-red-darken-1': editToken && !editToken.currentUserIsHolder }"
  >
    <span
      ><b>{{ t('interactionModes.editMode') }}</b></span
    >
    <v-switch
      color="green"
      hide-details
      :disabled="editToken && !editToken.currentUserIsHolder"
      :model-value="editToken?.currentUserIsHolder && interactionMode === InteractionModeEnum.EDIT"
      density="compact"
      @update:model-value="switchInteractionModes"
    ></v-switch>
    <v-progress-circular
      v-if="editTokenPending"
      :indeterminate="true"
      size="24"
      color="green"
    ></v-progress-circular>

    <span v-if="editToken && editToken.currentUserIsHolder && ttl">
      ({{ t('interactionModes.editModeTTL', { minutes: ttl.minutes, seconds: ttl.seconds }) }})
    </span>

    <span v-if="editToken && !editToken.currentUserIsHolder">
      {{
        t('interactionModes.contentBeingEdditedBy', {
          name: editToken?.firstName,
          surname: editToken?.familyName,
        })
      }}
    </span>
  </div>
</template>

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import { computed, onMounted, ref, watch } from 'vue';
  import { useInteractionModeStore } from '@/store/interactionModeStore';
  import { InteractionModeEnum } from '@/types/InteractionModeEnum';
  import { useRoute } from 'vue-router';
  import { getUserRoles } from '@/plugins/keycloak';

  interface TimeToLive {
    total: number;
    minutes: number | string;
    seconds: number | string;
  }

  const { t } = useI18n();
  const route = useRoute();
  const appEditable = ref(false);
  const interactionModeStore = useInteractionModeStore();
  const interactionMode = computed(() => interactionModeStore.mode);
  const editTokenPending = ref(false);
  const editToken = computed(() => {
    const token = interactionModeStore.editToken;
    const tokenPossesedAndActive = isTokenPossesedAndActive(token);

    const shouldEnterEditMode =
      tokenPossesedAndActive && interactionMode.value !== InteractionModeEnum.EDIT;
    if (shouldEnterEditMode) enterEditMode();

    const shouldEnterROMode =
      !tokenPossesedAndActive && interactionMode.value === InteractionModeEnum.EDIT;
    if (shouldEnterROMode) enterReadOnlyMode();

    _editModeCountDownUpd();

    return token;
  });

  const editEvtNames = [
    'keydown',
    'mousedown',
    'pointerdown',
    'touchstart',
    'wheel',
    'paste',
    'input',
  ];
  const editEvtOpts = { passive: true, capture: true };

  let timerID: number | undefined;
  let ttlID: number | undefined;
  const ttl = ref<TimeToLive | undefined>(undefined);

  onMounted(async () => {
    interactionModeStore.setMode(InteractionModeEnum.READONLY); // default mode

    appEditable.value = getUserRoles().includes('ria-editor');
    if (!appEditable.value) return;

    await fetchEditToken();
    await prolongEditMode();
  });

  watch(route, async () => {
    await fetchEditToken();
    await prolongEditMode();
  });

  async function switchInteractionModes() {
    if (editTokenPending.value) return;

    editTokenPending.value = true;
    if (interactionMode.value === InteractionModeEnum.READONLY)
      await interactionModeStore.requestEditMode();
    else if (interactionMode.value === InteractionModeEnum.EDIT)
      await interactionModeStore.leaveEditMode();
    editTokenPending.value = false;
  }

  async function fetchEditToken() {
    return await interactionModeStore.fetchEditToken();
  }

  async function prolongEditMode() {
    if (!isTokenPossesedAndActive(interactionModeStore.editToken)) return;
    await interactionModeStore.prolongEditMode();
  }

  function isTokenPossesedAndActive(token: EditTokenType | undefined) {
    if (!token) return;
    if (token?.currentUserIsHolder) return isTokenActive(token);
  }

  function isTokenActive(token: EditTokenType | undefined) {
    if (!token) return;
    return new Date(token?.exspire) > new Date();
  }

  async function enterEditMode() {
    const contentEl = document.querySelector('.content');
    if (!contentEl) return;

    for (const evtName of editEvtNames) {
      contentEl.addEventListener(evtName, editModeAutoProlong, editEvtOpts);
    }

    interactionModeStore.setMode(InteractionModeEnum.EDIT);
  }

  async function enterReadOnlyMode() {
    const contentEl = document.querySelector('.content');
    if (!contentEl) return;

    for (const evtName of editEvtNames) {
      contentEl.removeEventListener(evtName, editModeAutoProlong, editEvtOpts);
    }

    clearTimeout(timerID);
    timerID = undefined;
    interactionModeStore.setMode(InteractionModeEnum.READONLY);
  }

  async function editModeAutoProlong() {
    if (timerID) return;
    const token = editToken.value;
    let timeoutMS = 60000;
    if (isTokenPossesedAndActive(token)) {
      const now = new Date().getTime();
      const expire = new Date(token?.exspire as string).getTime();
      if (expire - now < 0) return (timerID = undefined); // token expired
      if (expire - now < timeoutMS) timeoutMS = (expire - now) / 2;
    }

    timerID = setTimeout(() => {
      timerID = undefined;
      if (isTokenPossesedAndActive(editToken.value)) prolongEditMode();
    }, timeoutMS);
  }

  function _editModeCountDownUpd() {
    const expireDt = editToken.value?.exspire;

    if (!expireDt) {
      clearInterval(ttlID);
      return (ttlID = undefined);
    }

    if (!ttlID) {
      ttlID = setInterval(() => {
        ttl.value = calculateTimeToEnd(editToken.value?.exspire);
        if (ttl.value && ttl.value.total <= 0) interactionModeStore.leaveEditMode();
      }, 1000);
    }
  }

  function calculateTimeToEnd(dateTimeStr?: string): TimeToLive | undefined {
    if (dateTimeStr) {
      let totalSeconds = Math.ceil((new Date(dateTimeStr).getTime() - new Date().getTime()) / 1000);

      return {
        total: totalSeconds,
        minutes: Math.floor(totalSeconds / 60),
        seconds: (totalSeconds % 60).toString().padStart(2, '0'),
      };
    }

    return undefined;
  }
</script>
